// Shot type options with descriptions (16 values — canonical)
export const SHOT_TYPES = [
  { value: "extreme-wide", label: "Extreme Wide Shot (EWS)", desc: "Establishes location, subject very small" },
  { value: "wide", label: "Wide Shot (WS)", desc: "Full body visible with environment" },
  { value: "full", label: "Full Shot (FS)", desc: "Character from head to toe" },
  { value: "medium-wide", label: "Medium Wide Shot (MWS)", desc: "Knees up, also called 3/4 shot" },
  { value: "cowboy", label: "Cowboy Shot", desc: "Mid-thigh up, hands visible" },
  { value: "medium", label: "Medium Shot (MS)", desc: "Waist up" },
  { value: "medium-close", label: "Medium Close-up (MCU)", desc: "Chest up" },
  { value: "close-up", label: "Close-up (CU)", desc: "Face fills frame" },
  { value: "extreme-close", label: "Extreme Close-up (ECU)", desc: "Eyes, mouth, or detail" },
  { value: "two-shot", label: "Two Shot", desc: "Two characters in frame" },
  { value: "over-shoulder", label: "Over the Shoulder (OTS)", desc: "One character's shoulder in foreground" },
  { value: "pov", label: "Point of View (POV)", desc: "From character's perspective" },
  { value: "insert", label: "Insert Shot", desc: "Close-up of object or detail" },
  { value: "cutaway", label: "Cutaway", desc: "Shot away from main action" },
  { value: "reaction", label: "Reaction Shot", desc: "Character's response" },
  { value: "establishing", label: "Establishing Shot", desc: "Sets time/place" },
];

// Camera angles (8 values — canonical)
export const CAMERA_ANGLES = [
  { value: "eye-level", label: "Eye Level", desc: "Neutral, natural perspective" },
  { value: "low-angle", label: "Low Angle", desc: "Camera below subject, looks up" },
  { value: "high-angle", label: "High Angle", desc: "Camera above subject, looks down" },
  { value: "dutch", label: "Dutch Angle", desc: "Tilted, creates unease" },
  { value: "birds-eye", label: "Bird's Eye", desc: "Directly overhead" },
  { value: "worms-eye", label: "Worm's Eye", desc: "Extreme low, looking straight up" },
  { value: "over-hip", label: "Over the Hip", desc: "Camera at hip level" },
  { value: "shoulder-level", label: "Shoulder Level", desc: "Slightly below eye level" },
];

// Camera movements (14 values — canonical, zoom split into zoom-in/zoom-out)
export const CAMERA_MOVEMENTS = [
  { value: "static", label: "Static", desc: "No movement" },
  { value: "pan", label: "Pan", desc: "Horizontal rotation on tripod" },
  { value: "tilt", label: "Tilt", desc: "Vertical rotation on tripod" },
  { value: "zoom-in", label: "Zoom In", desc: "Lens tightens focal length" },
  { value: "zoom-out", label: "Zoom Out", desc: "Lens widens focal length" },
  { value: "dolly", label: "Dolly", desc: "Camera moves toward/away" },
  { value: "truck", label: "Truck", desc: "Camera moves left/right" },
  { value: "pedestal", label: "Pedestal", desc: "Camera moves up/down" },
  { value: "tracking", label: "Tracking", desc: "Camera follows subject laterally" },
  { value: "crane", label: "Crane", desc: "Camera on crane arm" },
  { value: "handheld", label: "Handheld", desc: "Organic, shaky movement" },
  { value: "steadicam", label: "Steadicam", desc: "Smooth tracking shots" },
  { value: "arc", label: "Arc", desc: "Camera circles subject" },
  { value: "drone", label: "Drone", desc: "Aerial movement" },
];

// Lighting styles (17 values — canonical)
export const LIGHTING_STYLES = [
  { value: "natural", label: "Natural Light", desc: "Sun, window light" },
  { value: "soft", label: "Soft Light", desc: "Diffused, minimal shadows" },
  { value: "hard", label: "Hard Light", desc: "Sharp, defined shadows" },
  { value: "dramatic", label: "Dramatic", desc: "Strong contrast, theatrical" },
  { value: "high-key", label: "High Key", desc: "Bright, minimal shadows" },
  { value: "low-key", label: "Low Key", desc: "High contrast, dramatic shadows" },
  { value: "silhouette", label: "Silhouette", desc: "Subject backlit, dark outline" },
  { value: "backlit", label: "Backlit", desc: "Light from behind subject" },
  { value: "chiaroscuro", label: "Chiaroscuro", desc: "Renaissance-style light/dark contrast" },
  { value: "golden-hour", label: "Golden Hour", desc: "Warm, soft sunset/sunrise" },
  { value: "blue-hour", label: "Blue Hour", desc: "Cool twilight tones" },
  { value: "practical", label: "Practical", desc: "Light from visible sources" },
  { value: "motivated", label: "Motivated", desc: "Realistic to scene's sources" },
  { value: "rembrandt", label: "Rembrandt", desc: "Triangle of light on cheek" },
  { value: "split", label: "Split Lighting", desc: "Half face lit, half shadow" },
  { value: "butterfly", label: "Butterfly", desc: "Light from above, shadow under nose" },
  { value: "neon", label: "Neon/Colored", desc: "Vibrant colored lighting" },
];

// Lens options (10 values — canonical)
export const LENS_OPTIONS = [
  { value: "14mm", label: "14mm Ultra Wide", desc: "Extreme perspective, distortion" },
  { value: "24mm", label: "24mm Wide", desc: "Establishing shots, interiors" },
  { value: "35mm", label: "35mm Standard Wide", desc: "Natural perspective, versatile" },
  { value: "50mm", label: "50mm Normal", desc: "Closest to human eye" },
  { value: "85mm", label: "85mm Portrait", desc: "Flattering for faces" },
  { value: "100mm", label: "100mm Medium Tele", desc: "Compressed background" },
  { value: "135mm", label: "135mm Telephoto", desc: "Strong compression, intimate" },
  { value: "200mm", label: "200mm+ Long Tele", desc: "Extreme compression, surveillance" },
  { value: "macro", label: "Macro", desc: "Extreme close-up detail" },
  { value: "anamorphic", label: "Anamorphic", desc: "Cinematic wide aspect, flares" },
];

// "Three Stages of Seeing" — storyboard visualization stages
export const STORYBOARD_STAGES = [
  {
    value: "sketch",
    label: "Director's Sketch",
    icon: "sketch",
    desc: "Composition & blocking — the thinking stage",
    detail: "Loose charcoal/pencil, no detailed faces, emphasis on camera and blocking. 90% of storyboarding.",
  },
  {
    value: "concept",
    label: "Concept Art",
    icon: "concept",
    desc: "Color & atmosphere — the pitching stage",
    detail: "Full-color digital painting, mood and production design established. For pitch decks and lookbooks.",
  },
  {
    value: "previs",
    label: "Previsualization",
    icon: "previs",
    desc: "Photorealistic reference — the planning stage",
    detail: "Photorealistic rendering for VFX, lighting, and spatial planning. Not creative direction.",
    warning: "Previsualization generates specific visual interpretations. These are technical reference tools — not creative direction. Your film will not look like these images.",
  },
];

// Aspect ratio presets for film production
export const ASPECT_RATIOS = [
  { value: "2.39:1", label: "2.39:1 Anamorphic", desc: "Nolan, Villeneuve, epics" },
  { value: "2:1", label: "2:1 Univisium", desc: "Netflix preferred" },
  { value: "1.85:1", label: "1.85:1 Flat", desc: "Spielberg, most US films" },
  { value: "16:9", label: "16:9 Widescreen", desc: "TV & streaming standard" },
  { value: "4:3", label: "4:3 Academy", desc: "A24, The Lighthouse" },
  { value: "1:1", label: "1:1 Square", desc: "Instagram, experimental" },
  { value: "9:16", label: "9:16 Vertical", desc: "Social media, short-form" },
];

// Multi-frame configuration — movements not listed default to 1 frame (static)
export const MULTI_FRAME = {
  "pan":       { count: 2, labels: ["Start", "End"] },
  "tilt":      { count: 2, labels: ["Start", "End"] },
  "zoom-in":   { count: 2, labels: ["Wide", "Tight"] },
  "zoom-out":  { count: 2, labels: ["Tight", "Wide"] },
  "dolly":     { count: 3, labels: ["Start", "Mid", "End"] },
  "truck":     { count: 3, labels: ["Start", "Mid", "End"] },
  "pedestal":  { count: 2, labels: ["Low", "High"] },
  "tracking":  { count: 3, labels: ["Start", "Mid", "End"] },
  "crane":     { count: 3, labels: ["Start", "Mid", "End"] },
  "handheld":  { count: 1, labels: ["Single"] },
  "steadicam": { count: 3, labels: ["Start", "Mid", "End"] },
  "arc":       { count: 3, labels: ["Start", "Mid", "End"] },
  "drone":     { count: 3, labels: ["Start", "Mid", "End"] },
};

// Enum aliases for display normalization (old backend values → canonical)
export const ENUM_ALIASES = {
  "close": "close-up",
  "low": "low-angle",
  "high": "high-angle",
  "overhead": "birds-eye",
  "wide-16mm": "14mm",
  "wide-24mm": "24mm",
  "standard-35mm": "35mm",
  "normal-50mm": "50mm",
  "portrait-85mm": "85mm",
  "telephoto-135mm": "135mm",
  "zoom": "zoom-in",
};

/** Normalize an enum value using aliases */
export function normalizeEnum(value) {
  if (!value) return value;
  const v = value.trim().toLowerCase();
  return ENUM_ALIASES[v] || v;
}
